/* eslint-disable @typescript-eslint/no-explicit-any */

import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ApiError } from "@/app/common-types/api-error.types";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RemoveSureModal from "@/components/molecules/review-section/review-modals/remove-sure-popup";
import {
  SubmitVoucherCode,
  useSubmitCodes,
} from "@/hooks/api/review-api/apply-promo";
import { RemoveModalSchemaType } from "@/schema/renewal-page/payment-summary-page.schema";
import { RootState } from "@/store/root-reducer";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import handleError, { ErrorResponse } from "@/utils/handle-errors";

import { RowItemPrice } from "../price-show-row/price-show-row";

type PromoVoucherSectionProps = {
  removeModal: RemoveModalSchemaType;
  removeCta: string;
};

export function PromoVoucherSection({
  removeModal,
  removeCta,
}: Readonly<PromoVoucherSectionProps>) {
  const dispatch = useDispatch();
  const router = useRouter();
  const [removePromoVoucherData, setRemovePromoVoucherData] = useState<{
    type: "PROMO" | "VOUCHER";
    index?: number;
  }>();

  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [isOPenRemoveModal, setIsOpenRemoveModal] = useState(false);
  const { attributes, referenceId, type, version } = useSelector(
    (state: RootState) => state.quote
  );
  const { paymentDetails } = attributes;

  const [newQuote, submitCodes, errors]: any = useSubmitCodes(
    { version, type, attributes, referenceId },
    removePromoVoucherData?.type === "VOUCHER"
      ? paymentDetails?.discounts?.promoCode?.code ?? ""
      : "",
    removePromoVoucherData?.type === "VOUCHER"
      ? ([
          { code: "", index: removePromoVoucherData.index },
        ] as SubmitVoucherCode[])
      : [],
    setButtonDisabled
  );

  useEffect(() => {
    if (newQuote) {
      dispatch(updateQuote(newQuote));
      setIsOpenRemoveModal(false);
      setRemovePromoVoucherData(undefined);
    } else if (errors) {
      handleError(errors as ApiError<ErrorResponse>, router, dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, newQuote, errors]);

  const confirmRemovePromoVoucher = () => {
    submitCodes();
  };

  return (
    <>
      <RemoveSureModal
        content={removeModal}
        setIsOpen={setIsOpenRemoveModal}
        isOpen={isOPenRemoveModal}
        coverName=""
        submitRemove={confirmRemovePromoVoucher}
      />
      {attributes?.paymentDetails?.discounts?.promoCode?.valid && (
        <div className="mt-spacing-m mb-spacing-xs">
          <div>
            <RowItemPrice
              price={Math.abs(
                attributes?.paymentDetails?.discounts?.promoCode?.value
              )}
              title={attributes?.paymentDetails?.discounts?.promoCode?.code}
              minus="-"
              isSemiBold
            />

            <LabelButtonWrapper
              data-testid="promo-remove-button"
              id="promo-remove-button"
              label={removeCta}
              isDisabled={buttonDisabled}
              hierarchy="tertiary"
              className={` !px-0 text-[16px] font-body-font font-[600] leading-[24px] !text-dark-text-critical`}
              type="button"
              onClick={() => {
                setIsOpenRemoveModal(true);
                setRemovePromoVoucherData({
                  type: "PROMO",
                });
              }}
            />
          </div>
        </div>
      )}

      {paymentDetails?.discounts?.voucher?.map((item, index) =>
        !item.message ? (
          <div key={item.code} className=" mt-spacing-m mb-spacing-m ">
            <div>
              <RowItemPrice
                price={Math.abs(item.amount ?? 0)}
                title={item.code}
                minus="-"
                isSemiBold
              />

              <LabelButtonWrapper
                data-testid={`voucher-remove-button-${index}`}
                id={`voucher-remove-button-${index}`}
                label={removeCta}
                isDisabled={buttonDisabled}
                hierarchy="tertiary"
                className={` !px-0 text-[16px] font-body-font font-[600] leading-[24px] !text-dark-text-critical`}
                type="button"
                onClick={() => {
                  setIsOpenRemoveModal(true);
                  setRemovePromoVoucherData({
                    type: "VOUCHER",
                    index: item.index,
                  });
                }}
              />
            </div>
          </div>
        ) : null
      )}
    </>
  );
}
